import { useGeneralSettingsFormData } from "@/api/cms";
import { mergePlayerSettings } from "@/lib/media/catalogFullscreenPlayer";
import {
  buildEmbedSrc,
  DEFAULT_PLAYER_SETTINGS,
  resolveEmbedAspectRatio,
  resolveEmbedIds,
  type PlayerSettings,
} from "@/lib/media/embed";
import { cn } from "@/lib/utils";

interface EmbedPlayerProps {
  provider?: string;
  videoId?: string;
  embedUrl?: string;
  className?: string;
  title?: string;
  /** Compact layout for grid thumbnails (e.g. media library). */
  compact?: boolean;
  /** Per-attachment loop flag (combined with CMS playerLoop). */
  videoLoop?: boolean;
  /** Max height for portrait embeds (e.g. YouTube Shorts in CMS drawer). */
  portraitMaxHeight?: string;
  /** Fit inside a fixed-size parent without cropping (media library grid). */
  contain?: boolean;
  /** Render iframe immediately (dialogs/fullscreen — skip viewport lazy-load). */
  eager?: boolean;
  /** Overrides instance player settings (e.g. catalog fullscreen: controls + sound). */
  playerOverrides?: Partial<PlayerSettings>;
}

/**
 * Renders a YouTube/Vimeo iframe preview using CMS general player settings.
 * Uses a responsive aspect-ratio wrapper (16:9 or 9:16 for Shorts).
 */
export const EmbedPlayer = ({
  provider,
  videoId,
  embedUrl,
  className,
  title,
  compact = false,
  videoLoop,
  portraitMaxHeight,
  contain = false,
  playerOverrides,
}: EmbedPlayerProps) => {
  const { data: settings } = useGeneralSettingsFormData();
  const playerSettings = settings ?? DEFAULT_PLAYER_SETTINGS;
  const effectiveSettings = mergePlayerSettings(
    {
      ...playerSettings,
      playerLoop: Boolean(videoLoop || playerSettings.playerLoop),
    },
    playerOverrides,
  );

  const resolved = resolveEmbedIds(provider, videoId, embedUrl);
  const origin =
    typeof window !== "undefined" ? window.location.origin : undefined;
  const src = buildEmbedSrc(
    resolved.provider,
    resolved.videoId,
    effectiveSettings,
    origin,
  );

  if (!src) return null;

  const aspectRatio = resolveEmbedAspectRatio(embedUrl);
  const isPortrait = aspectRatio === "9 / 16";

  const iframe = (
    <iframe
      key={src}
      src={src}
      title={title ?? "video"}
      className="absolute inset-0 h-full w-full border-0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      // `origin` : sur http://localhost → https://youtube, strict-origin-when-cross-origin
      // n'envoie aucun Referer → YouTube Error 153 (« Une erreur s'est produite… »).
      referrerPolicy="origin"
      allowFullScreen
    />
  );

  if (contain) {
    return (
      <div
        className={cn(
          "flex h-full w-full items-center justify-center",
          className,
        )}
      >
        <div
          className="relative"
          style={{
            aspectRatio,
            maxHeight: "100%",
            maxWidth: "100%",
            ...(isPortrait
              ? { height: "100%", width: "auto" }
              : { width: "100%", height: "auto" }),
          }}
        >
          {iframe}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("w-full overflow-hidden rounded-md bg-black/5", className)}
    >
      <div
        className={cn("relative w-full", compact && "min-h-[120px]")}
        style={{
          aspectRatio,
          maxWidth: "100%",
          maxHeight:
            isPortrait && portraitMaxHeight ? portraitMaxHeight : undefined,
        }}
      >
        {iframe}
      </div>
    </div>
  );
};
